import { translateBackend } from '@assets/i18n/config';
import {
  CustomFieldTypes,
  type CustomFieldValue,
} from '@interfaces/customField';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { UserContext } from '@providers/userContextProvider';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

export const CustomFieldValueDisplay = ({
  field,
}: {
  field: CustomFieldValue;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const dateFormat =
    activeOrganization.applicableLocalization?.dateFormat ?? 'YYYY-MM-DD';
  const { t } = useTranslation(['workflowCompletions']);

  if (field.customField.type === CustomFieldTypes.BOOLEAN) {
    const typedField = field as CustomFieldValue<CustomFieldTypes.BOOLEAN>;

    if (typedField.value === undefined) return null;
    return (
      <Badge
        color={typedField.value ? 'positive' : 'negative'}
        label={t(`customFieldValues.boolean.${field.value}`)}
      />
    );
  }

  if (field.customField.type === CustomFieldTypes.SINGLE_SELECT) {
    const typedField =
      field as CustomFieldValue<CustomFieldTypes.SINGLE_SELECT>;
    const optionDisplays = groupBy(
      typedField.customField?.selectOptions,
      (o) => o.value,
    );

    if (typedField.value === undefined) return null;
    return (
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
        {translateBackend(
          optionDisplays?.[typedField.value]?.at(0)?.displayText,
        )}
      </Typography>
    );
  }

  if (field.customField.type === CustomFieldTypes.MULTIPLE_SELECT) {
    const typedField =
      field as CustomFieldValue<CustomFieldTypes.MULTIPLE_SELECT>;
    const optionDisplays = groupBy(
      typedField.customField?.selectOptions,
      (o) => o.value,
    );

    if (typedField.value === undefined) return null;
    return (
      <Box gap={1} display={'flex'}>
        {typedField.value.map((v) => (
          <Badge
            key={v}
            label={translateBackend(optionDisplays[v]?.at(0)?.displayText)}
          />
        ))}
      </Box>
    );
  }

  if (field.customField.type === CustomFieldTypes.DATE) {
    const typedField = field as CustomFieldValue<CustomFieldTypes.DATE>;

    if (typedField.value === undefined) return null;
    return (
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
        {dayjs(typedField.value).format(dateFormat)}
      </Typography>
    );
  }

  if (field.customField.type === CustomFieldTypes.CUSTOM_OBJECT) {
    /** @todo: implement this as part of https://onewealth.atlassian.net/browse/OV-19381 */
    return null;
  }

  return (
    <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
      {field.value}
    </Typography>
  );
};
